import React, { useState } from 'react';
import { 
  Award, 
  CheckCircle, 
  XCircle, 
  Plus,
  Edit,
  Trash2,
  Star,
  MapPin
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateVendorSpotlightModal from '../../components/admin/CreateVendorSpotlightModal';
import { useAdminVendorSpotlight } from '../../hooks/useAdminVendorSpotlight';
import { useToastContext } from '../../contexts/ToastContext';

const AdminVendorSpotlight = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSpotlight, setEditingSpotlight] = useState(null);
  const { success, error: showError } = useToastContext();
  const {
    spotlights,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    statistics,
    createSpotlight,
    isCreating,
    updateSpotlight,
    isUpdating,
    toggleStatus,
    isToggling,
    deleteSpotlight,
    isDeleting
  } = useAdminVendorSpotlight();

  // Define table columns - Limited to essential columns
  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: 'w-64',
      render: (spotlight) => (
        <div className="flex items-center gap-3">
          {spotlight.profile_image && (
            <img
              src={spotlight.profile_image}
              alt={spotlight.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          )}
          <div>
            <p className="text-sm font-medium text-gray-900">
              {spotlight.name || 'N/A'}
            </p>
            {spotlight.badge && (
              <span className="text-xs text-gray-500">{spotlight.badge}</span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-48',
      render: (spotlight) => (
        <p className="text-sm text-gray-900">
          {spotlight.category || 'N/A'}
        </p>
      )
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      width: 'w-48',
      render: (spotlight) => (
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <MapPin className="w-4 h-4" />
          {spotlight.location || 'N/A'}
        </div>
      )
    },
    {
      key: 'stats',
      label: 'Stats',
      sortable: false,
      width: 'w-48',
      render: (spotlight) => (
        <div className="text-xs text-gray-600 space-y-1">
          {spotlight.rating && (
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{spotlight.rating}</span>
              {spotlight.review_count && (
                <span>({spotlight.review_count})</span>
              )}
            </div>
          )}
          {spotlight.completed_events && (
            <div>{spotlight.completed_events} events</div>
          )}
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (spotlight) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          spotlight.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {spotlight.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (spotlight) => {
        setEditingSpotlight(spotlight);
        setShowCreateModal(true);
      }
    },
    {
      label: spotlight => spotlight.is_active ? 'Deactivate' : 'Activate',
      icon: spotlight => spotlight.is_active ? XCircle : CheckCircle,
      variant: 'default',
      onClick: (spotlight) => {
        toggleStatus(spotlight.id, {
          onSuccess: () => {
            success(
              'Status Updated', 
              `Vendor spotlight has been ${spotlight.is_active ? 'deactivated' : 'activated'} successfully.`
            );
          },
          onError: (error) => {
            const errorMessage = error?.response?.data?.message || 'Failed to update status.';
            showError('Status Update Failed', errorMessage);
          }
        });
      },
      disabled: isToggling
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (spotlight) => {
        if (window.confirm('Are you sure you want to delete this vendor spotlight?')) {
          deleteSpotlight(spotlight.id, {
            onSuccess: () => {
              success('Vendor Spotlight Deleted', 'The vendor spotlight has been deleted successfully.');
            },
            onError: (error) => {
              const errorMessage = error?.response?.data?.message || 'Failed to delete vendor spotlight.';
              showError('Deletion Failed', errorMessage);
            }
          });
        }
      },
      disabled: isDeleting
    }
  ];

  const handleCreate = (data) => {
    createSpotlight(data, {
      onSuccess: () => {
        setShowCreateModal(false);
        setEditingSpotlight(null);
        success('Vendor Spotlight Created', 'The vendor spotlight has been created successfully.');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to create vendor spotlight.';
        showError('Creation Failed', errorMessage);
      }
    });
  };

  const handleUpdate = (data) => {
    updateSpotlight(
      { id: editingSpotlight.id, data },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setEditingSpotlight(null);
          success('Vendor Spotlight Updated', 'The vendor spotlight has been updated successfully.');
        },
        onError: (error) => {
          const errorMessage = error?.response?.data?.message || 'Failed to update vendor spotlight.';
          showError('Update Failed', errorMessage);
        }
      }
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Vendor Spotlights</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load vendor spotlights. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Award className="w-6 h-6" />
                Vendor Spotlight
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage featured vendor spotlights displayed on the homepage
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingSpotlight(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Spotlight
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Spotlights</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {statistics.total_spotlights || 0}
                </p>
              </div>
              <Award className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {statistics.active_spotlights || 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">
                  {statistics.inactive_spotlights || 0}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              options={activeStatusOptions}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={spotlights}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search by name, category, description, or location..."
            onSearch={(searchTerm) => handleFilterChange('search', searchTerm)}
            emptyMessage="No vendor spotlights found"
            pageSize={10}
            showPagination={true}
          />
        </div>

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <CreateVendorSpotlightModal
            isOpen={showCreateModal}
            onClose={() => {
              setShowCreateModal(false);
              setEditingSpotlight(null);
            }}
            onSave={editingSpotlight ? handleUpdate : handleCreate}
            spotlight={editingSpotlight}
            isEdit={!!editingSpotlight}
            isLoading={isCreating || isUpdating}
          />
        )}
      </div>
    </div>
  );
};

export default AdminVendorSpotlight;

